export default customElements.define('w-field-file', class FieldFile extends wealthify.componentBases.FieldUserDefined {
    constructor() {
        const config = {
            hasChildren: true,
            defaultSlotContents: {
                uploadbutton: '<w-button-2 label="Browse files" variant="secondary" fullwidth="true"  data-slot="uploadbutton" data-uploadbutton></w-button-2>',
                uploadbuttonmobile: '<w-button-2 label="Upload files" iconright="upload" variant="secondary" fullwidth="true"  data-slot="uploadbuttonmobile" data-uploadbuttonmobile></w-button-2>',
            },
        };

        super(config);

        this.onConnectedCallback = () => {
            this.classList.add(this.styleSelector);
            this.#setupUploadButtons();

            this.dom.uploadbutton.addEventListener('click', this.#handleClick.bind(this));
            this.dom.uploadbuttonmobile.addEventListener('click', this.#handleClick.bind(this));
            this.dom.fieldinput.addEventListener('change', this.#handleValueChange.bind(this));
            // If user clicks any labels (that will result in input click)
            this.dom.fieldinput.addEventListener('click', this.#handleClick.bind(this));

            // Required to attach an event to the closing of the file directory
            window.addEventListener('focus', this.#handleFileDirectoryClose.bind(this));

            // Remove blur event from Field base since input is blurred when file directory is displayed
            this.removeEventListener('blur', this.validation.events.StatusDismiss.dispatch, true);

            this.addEventListener(this.events.DisabledToggled.name, this.#disableButtons);
            this.addEventListener(this.events.ReadOnlyToggled.name, this.#handleReadOnly);

            this.addEventListener('drop', (event) => {
                this.#handleDragDrop(event);
            }, true);

            this.addEventListener('dragover', function (event) {
                event.preventDefault();
                event.stopPropagation();
            }, true);

            this.#toggleLabels();
            this.#handleReadOnly();
        };
    }

    //static
    static styleSelector = 'c-field-file';

    static template = `
                <div data-root>
                    <div data-fieldlabelcontainer>
                        <label class="u-display__hidden" data-fieldlabel></label>
                    </div>
                    <p class="u-display__hidden" data-fieldlabelpre></p>
                    <div data-interactive-container>
                        <div class="${this.styleSelector}__dropcontainer u-display__none--mobile" data-drop-container>
                            <input type="file" class="${this.styleSelector}__input u-display__hidden" data-fieldinput />
                            <div class="u-display__flex u-flex__alignitems--center u-flex__justifycontent--center">
                                <div>
                                    <p class="u-font__size--300 u-text__weight--bold u-text__align--center">Drag and drop your files here</p>
                                    <p class="u-text__align--center">or</p>
                                    <div class="u-display__flex u-flex__justifycontent--center">
                                        <span data-slot="uploadbutton" data-uploadbutton></span>
                                    </div>
                                </div>
                            </div>
                            <span data-statusaria></span>
                        </div>
                        <div class="u-display__none--tablet u-display__none--desktop">
                            <span data-slot="uploadbuttonmobile" data-uploadbuttonmobile></span>
                        </div>
                        <div class="${this.styleSelector}__requirements u-display__flex u-flex__justifycontent--space-between u-size__width--100">
                            <p class="u-font__size--200 u-margin__bottom-mobile--0">Supported formats: <span data-accepted-file-types>Any</span></p>
                            <p class="u-margin__bottom-mobile--spacing-m">Maximum size: <span data-max-file-size>5</span>MB</p>
                        </div>
                    </div>
                    <div class="u-display__none" data-attached-files-container>
                        <p class="u-text__weight--bold">Attached files</p>
                        <div data-filelist></div>
                    </div>
                    <p class="u-display__hidden" data-fieldlabelsub></p>
                    <div class="u-flex__alignitems--center u-margin__top-mobile--spacing-xl">
                        <w-icon size="200" data-statusicon></w-icon>
                        <w-message class="u-margin__top-mobile--0" aria-live="assertive" data-fieldmessage></w-message>
                    </div>
                </div>`;

    static get validCaptureOptions() {
        return ['user', 'environment'];
    }

    static get observedProps() {
        return [
            new wealthify.Prop('acceptprop', {}),
            new wealthify.Prop('multipleprop', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('captureprop', {
                nullable: true,
                type: wealthify.propTypes.ArrayItem(this.validCaptureOptions)
            }),
            new wealthify.Prop('maxfilesize', {
                default: 5120, //5120KB = 5MB
                type: wealthify.propTypes.Number()
            }),
            new wealthify.Prop('hidelabels', {
                default: false,
                type: wealthify.propTypes.Boolean()
            })
        ];
    }

    static get formatEnums() {
        return { Image: 'image' };
    }

    static get formatDictionary() {
        return {[this.formatEnums.Image]: {
            maxfilesize: 10240,
            acceptprop: 'image/png,image/jpg,image/jpeg,application/pdf,image/heif',
        }};
    }

    // public
    get propCallbacks() {
        return {
            acceptprop: () => {
                this.dom.fieldinput.setAttribute('accept', this.acceptprop);
                this.#updateAcceptedFormatsUI();
            },
            multipleprop: () => {
                if (this.multipleprop) {
                    this.dom.fieldinput.setAttribute('multiple', this.multipleprop);
                } else {
                    this.dom.fieldinput.removeAttribute('multiple');
                }
            },
            captureprop: () => {
                this.dom.fieldinput.setAttribute('capture', this.captureprop);
            },
            maxfilesize: () => {
                this.#updateMaxFileSizeUI();
            },
            hidelabels: () => this.#toggleLabels()
        };
    }

    get files() {
        return this.dom.fieldinput.files;
    }

    get acceptedFileTypes() {
        return this.acceptprop.split(',');
    }

    set files(dataTransfer) {
        this.#fileList = dataTransfer;
        this.dom.fieldinput.files = dataTransfer.files;
        this.#handleValueChange();
    }

    // private
    #fileList = new DataTransfer();
    #directoryOpened = false;
    #validStatusTimeout = 1500;
    #noValueChangeCallback;
    #megabyteInKilobytes = 1024;
    #fileItemCardTemplate = `
        <div class="u-size__width-mobile--100 u-size__width-tablet--50 u-padding__mobile--spacing-m">
            <w-card>
                <div class="u-display__flex u-flex__gap-mobile--spacing-m u-flex__alignitems--center u-flex__justifycontent--space-between">
                    <div>
                        <p class="u-text__weight--bold u-margin__bottom-mobile--spacing-xs">{{filename}}</p>
                        <p>{{filesize}}KB</p>
                    </div>
                    <div style="flex-shrink: 0;">
                        <w-button-2 label="Remove" variant="tertiary" iconright="bin" data-index="{{index}}"data-remove-button></w-button-2>
                    </div>
                </div>
            </w-card>
        </div>
    `;


    #toggleLabels() {
        const classListAction = this.hidelabels ? 'add' : 'remove';

        this.dom.fieldlabel.classList[classListAction]('u-display__hidden');
        this.dom.fieldlabelpre.classList[classListAction]('u-display__hidden');
        this.dom.fieldlabelsub.classList[classListAction]('u-display__hidden');
    }

    #handleClick() {
        this.#directoryOpened = true;
        this.dom.fieldinput.click();
    }

    #handleFileDirectoryClose() {
        if (!this.#directoryOpened) {

            return;
        }

        this.#directoryOpened = false;

        // The only way of knowing whether field value hasn't changed after file directory close
        // is if file input's change event does not run afterward -
        // setTimeout method will run to handle this, unless cancelled by change event first
        this.#noValueChangeCallback = setTimeout(this.#handleNoValueChange.bind(this), 500);
    }

    #handleNoValueChange() {
        if (this.value) {
            // Reapply value if previous file has been reselected - change event will be triggered
            this.dom.fieldinput.dispatchEvent(new Event('change'));
        } else {
            // Fire validation if value is empty and user presses Cancel
            this.validation.events.ToggleInteractedWith.dispatch();
            this.validation.events.FireValidation.dispatch();
        }
    }

    #handleValueChange() {
        Object.values(this.files).forEach(function (item) {
            if (!Object.values(this.#fileList.files).includes(item)) {

                if (this.multipleprop) {
                    this.#fileList.items.add(item);
                } else {
                    this.#fileList = new DataTransfer();
                    this.#fileList.items.add(item);
                }
            }
        }, this);

        this.dom.fieldinput.files = this.#fileList.files;
        this.#updateFileListUI();

        const withinMaxFileSize = Array.from(this.files).every(file => (file.size / this.#megabyteInKilobytes) <= this.maxfilesize);

        clearTimeout(this.#noValueChangeCallback);

        if (withinMaxFileSize) {
            this[wealthify.FieldValidation.messagePropEnums.Custom] = '';
        } else {
            this[wealthify.FieldValidation.messagePropEnums.Custom] = `This file is too large. You can only upload file up to ${this.maxfilesize / 1024}MB in size. Please select another file or alternatively you can take a photo, if your device allows.`;

            return;
        }

        this.events.ValueChange.dispatch();

        if (this.valid) {
            // Dismiss success state (usually done on blur of other Field components but not the same here)
            // Also valuechange event is async
            setTimeout(() => this.validation.events.StatusDismiss.dispatch(), this.#validStatusTimeout);

            if (this.dom.fieldinput.value) {
                this.#dispatchFileSelectedEvent();
            }
        }
    }

    #setupUploadButtons() {
        // tablet / desktop button
        if (this.dom.uploadbutton.tagName.toLowerCase() === 'span') {
            return;
        }

        // TODO - Consider during slot refactor (PDB-1008)
        if (this.dom.uploadbutton.tagName.toLowerCase() !== 'w-button-2') {
            return wealthify.error.throwError(this.tagname, `The slot content for "uploadbutton" in the "${this.tagName}" component can only be the "w-button-2" component.`);
        }

        // mobile buttons
        if (this.dom.uploadbuttonmobile.tagName.toLowerCase() === 'span') {
            return;
        }

        // TODO - Consider during slot refactor (PDB-1008)
        if (this.dom.uploadbuttonmobile.tagName.toLowerCase() !== 'w-button-2') {
            return wealthify.error.throwError(this.tagname, `The slot content for "uploadbuttonmobile" in the "${this.tagName}" component can only be the "w-button-2" component.`);
        }

        this.#disableButtons();
    }

    #dispatchFileSelectedEvent() {
        this.dispatchEvent(new Event('fileselected'));
    }

    #disableButtons() {
        this.dom.uploadbutton.disabledProp = this.disabledprop || this.readonlyprop;
        this.dom.uploadbuttonmobile.disabledProp = this.disabledprop || this.readonlyprop;

        [...this.querySelectorAll('[data-remove-button')].forEach((btn) => btn.disabledProp = this.disabledprop || this.readonlyprop);
    }

    #handleReadOnly() {
        this.#disableButtons();

        const classListAction = this.readonlyprop ? 'add' : 'remove';

        this.dom.interactiveContainer.classList[classListAction]('u-display__none');
        [...this.querySelectorAll('[data-remove-button')].forEach((btn) => btn.classList[classListAction]('u-display__none'));
    }

    #handleDragDrop(event) {
        event.preventDefault();
        event.stopPropagation();
        this.#addFileToFileList(event.dataTransfer.files);
    }

    #addFileToFileList(files) {
        if (files?.length > 1 && !this.multipleprop) {
            this[wealthify.FieldValidation.messagePropEnums.Custom] = 'Too many files, please select 1 file. No files have been added.';

            return;
        } else {
            this[wealthify.FieldValidation.messagePropEnums.Custom] = '';
        }

        if (files?.length) {
            if (!this.multipleprop) {
                if (this.#isValidFileType(files[0])) {
                    this.#fileList = new DataTransfer();
                    this.#fileList.items.add(files[0]);
                }
            } else {
                Object.values(files).forEach(function (file) {
                    // ignore files already added and check valid file type
                    if (!Object.values(this.#fileList.files).map((f) => f.name)
                        .includes(file.name) && this.#isValidFileType(file)) {
                        this.#fileList.items.add(file);
                    }
                }, this);
            }
            this.dom.fieldinput.files = this.#fileList.files;
            this.#handleValueChange();
        }
    }

    #handleRemove(event) {
        let button = event.currentTarget;

        this.#fileList.items.remove(button.dataset.index);
        this.dom.fieldinput.files = this.#fileList.files;
        this.#handleValueChange();
    }

    #updateFileListUI() {
        const attachedFilesClassListAction = this.files?.length ? 'remove' : 'add';

        this.dom.attachedFilesContainer.classList[attachedFilesClassListAction]('u-display__none');

        const frag = document.createDocumentFragment();
        const fileListUIWrapper = document.createElement('div');

        fileListUIWrapper.classList.add('u-display__flex');
        fileListUIWrapper.classList.add('u-flex__wrap');

        Object.values(this.files).forEach(function (file, index) {

            const fileItemCardTemplate = this.#fileItemCardTemplate
                .replace('{{filename}}', file.name)
                .replace('{{filesize}}', (file.size / 1024).toFixed(1))
                .replace('{{index}}', index);

            const fileListUIItem = wealthify.dom.fromString(fileItemCardTemplate);
            const removeButton = fileListUIItem.querySelector('[data-remove-button]');

            removeButton.addEventListener('click', this.#handleRemove.bind(this));

            fileListUIWrapper.appendChild(fileListUIItem);
            frag.appendChild(fileListUIWrapper);
        }, this);

        this.dom.filelist.replaceChildren(frag);
    }

    #updateMaxFileSizeUI() {
        this.dom.maxFileSize.innerText = this.maxfilesize / this.#megabyteInKilobytes;
    }

    #updateAcceptedFormatsUI() {
        this.dom.acceptedFileTypes.innerText = this.acceptprop.replace(/[a-zA-Z]*\//g, ' ').toUpperCase();
    }

    #isValidFileType(file) {
        return !this.acceptprop || this.acceptedFileTypes.includes(file.type);
    }
});